import React, { useEffect } from "react";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Brush,
  ReferenceLine,
  AreaChart,
  Area,
  LineChart,
  Line,
  ReferenceDot,
} from "recharts";
import { getGraphHeight } from "utils/helpers";
import { appColors, downTheLine, fullSwing } from "utils/constants";
import { ConditionalRenderer } from "components/conditonal-renderer";
const DesktopVisualizeChart = ({
  leadWrist,
  clubFitting,
  setIndexes,
  brushIndexes,
  data,
  dataKeys,
  yAxisTitle,
  width,
  height,
  showBrush,
  referenceLines,
  timeAtEveryPosition,
  isForcesGraph,
  swing,
  fill,
  opacity,
  linesColors,
  dotted,
  hideLegend,
  isBallGraph,
  variant,
  xAxisTitle = "Time (s)",
  hideXLabels,
  handleClickedTime,
  hideYlables,
  showDotAtRefernecLine,
}) => {
  const minYMaxYValues = {
    minYValueSet1: 0,
    maxYValueSet1: 0,
    minYValueSet2: 0,
    maxYValueSet2: 0,
    minYValueSet3: 0,
    maxYValueSet3: 0,
  };
  const length = data?.labels?.length;
  let dataForMultiple;
  function getMappedDataForRecharts(data) {
    return data?.labels?.map((label, index) => {
      const obj = { time: label };
      data.datasets.forEach((dataset) => {
        const { label: datasetLabel, data: datasetData } = dataset;

        if (dataset.data !== undefined) {
          obj[datasetLabel.toLowerCase()] = datasetData[index];
        } else {
          obj[datasetLabel.toLowerCase()] = null; // or any other default value you prefer
        }
      });
      return obj;
    });
  }
  function getMinYValue({ data, dataForRecharts }) {
    let minYValue = 0;
    if (dataForRecharts?.length > 0) {
      // Check if datasets array exists
      if (data?.datasets && Array.isArray(data.datasets)) {
        const minValues = data.datasets.map(({ data }) => {
          // Check if data array exists and is not empty
          if (data && Array.isArray(data) && data.length > 0) {
            return Math.min(...data);
          } else {
            return 0; // Default value when data is undefined or empty
          }
        });

        minYValue = Math.floor(Math.min(...minValues) / 10) * 10;
        // Ensure minYValue is not greater than 0
        minYValue = minYValue > 0 ? 0 : minYValue;
      }
    }
    return minYValue;
  }

  function getMaxYValue({ data, dataForRecharts }) {
    let maxYValue = 0;
    if (dataForRecharts?.length > 0) {
      // Check if datasets array exists
      if (data.datasets && Array.isArray(data.datasets)) {
        const maxValues = data.datasets.map(({ data }) => {
          // Check if data array exists and is not empty
          if (data && Array.isArray(data) && data.length > 0) {
            return Math.max(...data);
          } else {
            return 0; // Default value when data is undefined or empty
          }
        });

        maxYValue = Math.ceil(Math.max(...maxValues) / 10) * 10;
      }
    }

    return maxYValue;
  }
  const numberOfSets = 3;

  // Create dataForRecharts for each set
  if (leadWrist && data?.labels?.length > 0) {
    const labelOrder = [
      "thrust",
      "sway",
      "lift",
      "wrist shaft",
      "extension/flexion",
      "speed",
    ];
    // Sort datasets based on label order
    const sortedDatasets = data.datasets.sort(
      (a, b) => labelOrder.indexOf(a.label) - labelOrder.indexOf(b.label)
    );

    const sets = [
      { labels: data.labels, datasets: sortedDatasets.slice(0, 3) },
      { labels: data.labels, datasets: sortedDatasets.slice(3, 5) },
      { labels: data.labels, datasets: sortedDatasets.slice(5) },
    ];
    sets.forEach((setData, index) => {
      let dataForRecharts = getMappedDataForRecharts(setData);

      if ((dataForRecharts, setData)) {
        const min = getMinYValue({ data: setData, dataForRecharts });
        const max = getMaxYValue({ data: setData, dataForRecharts });
        minYMaxYValues[`minYValueSet${index + 1}`] = min;
        minYMaxYValues[`maxYValueSet${index + 1}`] = max;
      }
    });
    dataForMultiple = sets.map((setData) => getMappedDataForRecharts(setData));
  }

  const dataForRecharts = getMappedDataForRecharts(data);

  const linesColorLocal = {
    line1: appColors.yellow,
    line2: appColors.green,
    line3: appColors.blue,
    line4: "#7AA2AD",
  };
  const brushElement = document.getElementsByClassName(
    "recharts-brush-slide"
  )[0];
  const brushTraveller = document.querySelectorAll(
    ".recharts-brush-traveller rect"
  );

  useEffect(() => {
    if (brushElement && brushTraveller) {
      brushElement.style.transition = "all 0.3s linear";
      brushTraveller[0].style.transition = "all 0.3s linear";
      brushTraveller[1].style.transition = "all 0.3s linear";
      brushElement.style.transitionDelay = "0.01s";
      brushTraveller[0].style.transitionDelay = "0.01s";
      brushTraveller[1].style.transitionDelay = "0.01s";
    }
  }, [brushElement, brushTraveller]);

  function handleBrushChange(e) {
    setTimeout(() => {
      setIndexes(e);
    }, 300);
  }

  const formatTooltip = (value) => {
    return value?.toFixed(2);
  };
  const formatXAxisValue = (value) => {
    const parsedValue = parseFloat(value);
    if (!isNaN(parsedValue)) {
      return `Time: ${parsedValue.toFixed(2)}s`;
    }
  };
  const sortedReferenceLines = referenceLines?.sort((a, b) => a - b); // Sort reference lines in ascending order

  let maxValue = null;
  let minXValue = null;
  if (dataForRecharts) {
    maxValue = Math.max(...dataForRecharts.map((entry) => entry.time));
    minXValue = Math.min(...dataForRecharts.map((entry) => entry.time));
  }

  function shouldSkipLine(swing, index) {
    const indexesToHide = [1, 2, 4, 5, 7, 8, 9];

    return (
      index === 1 ||
      index === 2 ||
      index === 4 ||
      index === 5 ||
      index === 7 ||
      index === 8 ||
      index === 9
    );
  }

  // Function to calculate the label value based on conditions
  function calculateLabelValue(swing, index) {
    if (swing.swingType !== "full swing") {
      // For non-full swing, label lines as P1, P4, P7, and P10
      const labels = ["P1", "P4", "P7", "P10"];
      return labels[index % 4];
    }
    if (
      swing.swingType === fullSwing &&
      swing.orientation === downTheLine &&
      index === 7
    ) {
      return "P10";
    }
    return `P${index + 1}`;
  }

  let minYValue = getMinYValue({ data, dataForRecharts });
  let maxYValue = getMaxYValue({ data, dataForRecharts });

  // Ensure minYValue is not greater than 0
  minYValue = minYValue > 0 ? 0 : minYValue;

  const key1LineColor = linesColors?.line1 || linesColorLocal?.line1;
  const key2LineColor = linesColors?.line2 || linesColorLocal?.line2;
  const key3LineColor = linesColors?.line3 || linesColorLocal?.line3;
  const key4LineColor = linesColors?.line4 || linesColorLocal?.line4;
  const key5LineColor = linesColors?.line5 || linesColorLocal?.line1;
  const key6LineColor = linesColors?.line6 || linesColorLocal?.line2;

  const listofLineColors = {
    sway: "#f4bd00",
    lift: "#7bb728",
    thrust: "#006ab3",
    wrist: "#cbcbd2",
    extension: "#607d8b",
    speed: "#006ab3",
  };

  const listOfGraphLines = [
    {
      dataKey: dataKeys?.dataKey1,
      color: key1LineColor,
      dotted: dotted?.line1,
    },
    {
      dataKey: dataKeys?.dataKey2,
      color: key2LineColor,
      dotted: dotted?.line2,
    },
    {
      dataKey: dataKeys?.dataKey3,
      color: key3LineColor,
      dotted: dotted?.line3,
    },
    {
      dataKey: dataKeys?.dataKey4,
      color: key4LineColor,
      dotted: dotted?.line4,
    },
    {
      dataKey: dataKeys?.dataKey5,
      color: key5LineColor,
      dotted: dotted?.line5,
    },
    {
      dataKey: dataKeys?.dataKey6,
      color: key6LineColor,
      dotted: dotted?.line6,
    },
  ].filter((state) => !!state.dataKey);

  const PValueLinesData =
    sortedReferenceLines &&
    referenceLines &&
    swing &&
    sortedReferenceLines
      .filter((line, index) => !shouldSkipLine(swing, index))
      .map((line, index) => {
        const labelValue = calculateLabelValue(swing, index);

        return {
          value: line,
          color: "red",
          label: labelValue,
          thickness: 2,
          labelPlacement: "outside",
          labelAlign: "far",
        };
      });

  // Now you can use PValueLinesData in your Recharts component
  // <ChildGraph data={PValueLinesData} />

  function getHeightBasedOnVariant(variant) {
    if (variant === "launch-monitor") {
      return "inherit";
    }
    return getGraphHeight();
  }
  const capitalizeWords = (string) => {
    return string.replace(/\b\w/g, (char) => char.toUpperCase());
  };
  function handleClick(e) {
    handleClickedTime(e.activeLabel);
  }

  // Here we find the y value for showing dot on refernce line
  const matchingDataPoint = dataForRecharts.find((point) => {
    return point.time === timeAtEveryPosition;
  });

  const yValue = matchingDataPoint ? matchingDataPoint[dataKeys?.dataKey1] : 0; // default to 0 if no matching point

  return (
    <div
      style={{
        width: "100%",
        height,
      }}
    >
      <ResponsiveContainer width="100%" height="100%">
        {!isBallGraph ? (
          <AreaChart
            width={width || 590}
            height={height || 200}
            data={dataForRecharts}
            // margin={{ top: 20, right: 30, left: 0, bottom: 10 }}
            style={{ overflow: "hidden" }}
            onClick={handleClick}
          >
            {/* <CartesianGrid strokeDasharray="3 3" /> */}
            <XAxis
              tick={<CustomizedTickX hideXLabels={hideXLabels} />}
              dataKey="time"
              type="number"
              domain={[minXValue || 0, maxValue]} // Set the X-axis domain to start from 0 and go up to the maximum value
              label={{
                value: xAxisTitle,
                position: "insideLeft",
                offset: "-55",
                fontSize: 10,
                fontWeight: 700,
              }}
            />
            <YAxis
              hide={hideYlables}
              tick={<CustomizedTick hideXLabels={hideXLabels} />}
              // yAxisId="left"
              // interval="preserveStartEnd"
              // tickCount={5}
              domain={[minYValue, maxYValue]}
              label={{
                value: `${yAxisTitle}`,
                angle: -90,
                position: "insideBottomLeft",
                fontSize: 10,
                fontWeight: 700,
                offset: "-5",
              }}
            />

            <Tooltip
              // cursor={false}
              formatter={(value, name) => [
                formatTooltip(value),
                capitalizeWords(name),
              ]}
              labelFormatter={(value) => formatXAxisValue(value)}
              offset={25}
              position={{ y: clubFitting && 0 }}
            />
            {/* <Legend
              payload={hideLegend && []}
              layout="horizontal"
              align="center"
              verticalAlign="top"
            /> */}

            {listOfGraphLines?.map((item, index) => {
              return (
                <Area
                  key={index}
                  type="monotone"
                  fillOpacity={0.2}
                  baseValue={minYValue}
                  dataKey={item?.dataKey}
                  stroke={item?.color}
                  fill={item?.color}
                  strokeDasharray={item.dotted ? (3, 3) : (0, 0)}
                />
              );
            })}

            {sortedReferenceLines &&
              referenceLines &&
              swing &&
              sortedReferenceLines.map((line, index) => {
                // position lines to hide by their indexes
                const linesToHide = [1, 2, 4, 5, 7, 8, 9];
                if (linesToHide.includes(index)) {
                  return null;
                }

                const labelValue = calculateLabelValue(swing, index);

                return (
                  <ReferenceLine
                    key={index}
                    x={line}
                    stroke="blue"
                    label={{
                      value: labelValue,
                      position: "top",
                      fontWeight: "bold",
                      fontSize: "10px",
                    }}
                  />
                );
              })}

            <ReferenceLine x={timeAtEveryPosition} stroke="white" />
            {showDotAtRefernecLine && (
              <ReferenceDot
                strokeWidth={2}
                x={timeAtEveryPosition}
                r={4} // radius of the dot
                y={yValue}
                fill="#006ab3" // color of the dot
                stroke="white" // if you don't want any border around the dot
                isFront={true} // to ensure the dot is in front of the line
              />
            )}
            {/* <ReferenceLine
              y={0}
              stroke="#006ab3"
              strokeWidth={1.5}
              strokeDasharray="[]"
            /> */}
          </AreaChart>
        ) : (
          <LineChart
            width={400}
            height={300}
            data={data}
            margin={{ top: 20, right: 30, left: 0, bottom: 10 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="distance" type="number" />
            <YAxis dataKey="height" type="number" />
            <Tooltip
              formatter={formatTooltip}
              labelFormatter={(value) => formatXAxisValue(value)}
              offset={25}
              position={{ angle: clubFitting && 0 }}
            />
            <Line
              type="monotone"
              dataKey="height"
              stroke="#8884d8"
              dot={{ r: 0 }}
            />
          </LineChart>
        )}
      </ResponsiveContainer>
    </div>
  );
};
const CustomizedTick = (props) => {
  const { x, y, payload, hideXLabels } = props;
  return (
    <text
      x={x}
      y={y}
      fill="black"
      textAnchor="middle"
      dx={-10}
      style={{ fontWeight: hideXLabels ? "bold" : "normal" }}
    >
      {payload.value}
    </text>
  );
};
const CustomizedTickX = (props) => {
  const { x, y, payload, hideXLabels } = props;
  return (
    <text
      x={x}
      y={y}
      fill="black"
      textAnchor="middle"
      dy={10}
      style={{ display: hideXLabels ? "none" : "block" }}
    >
      {payload.value}
    </text>
  );
};

export default React.memo(DesktopVisualizeChart);
